import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PillTabs, QueueCard, StatusPill } from "@/components/lux";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WorkOrderDialog from "@/mechanic/components/WorkOrderDialog";
import ImportPdfDialog from "@/mechanic/components/ImportPdfDialog";
import { Plus, Pencil, Trash2, Search, ExternalLink, FileUp } from "lucide-react";

const statusStyle = {
  "Estimate": "bg-slate-500/12 text-slate-400", "Approved": "bg-cyan-500/12 text-cyan-400",
  "In Progress": "bg-blue-500/12 text-blue-400", "Waiting on Parts": "bg-purple-500/12 text-purple-400",
  "Ready": "bg-green-500/12 text-green-400", "Picked Up": "bg-emerald-500/12 text-emerald-400",
  "Closed": "bg-green-500/12 text-green-400", "Open": "bg-amber-500/12 text-amber-400",
  "Awaiting Parts": "bg-purple-500/12 text-purple-400", "Completed": "bg-green-500/12 text-green-400",
};

const STATUS_TONE = {
  Estimate: "neutral", Approved: "blue", "In Progress": "amber", "Waiting on Parts": "violet",
  "Awaiting Parts": "violet", Ready: "green", "Picked Up": "green", Closed: "green",
  Completed: "green", Open: "amber",
};

const FILTER_STATUSES = ["Estimate", "Approved", "In Progress", "Waiting on Parts", "Ready", "Picked Up", "Closed", "Open", "Awaiting Parts", "Completed"];

export default function WorkOrders() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data: wos = [], isLoading } = useQuery({
    queryKey: ["mech-wos", statusFilter],
    queryFn: async () => (await api.get("/work-orders", { params: statusFilter !== "all" ? { status: statusFilter } : {} })).data,
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return wos;
    return wos.filter((w) =>
      (w.vehicle_label || "").toLowerCase().includes(s)
      || (w.customer_name || "").toLowerCase().includes(s)
      || (w.complaint || "").toLowerCase().includes(s)
      || (w.diagnosis || "").toLowerCase().includes(s)
      || (w.status || "").toLowerCase().includes(s)
    );
  }, [wos, q]);

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/work-orders/${id}`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Work order deleted"); },
  });

  const active = useMemo(
    () => filtered.filter((w) => ["Open", "In Progress", "Awaiting Parts", "Waiting on Parts", "Approved"].includes(w.status)),
    [filtered]
  );
  const daysIn = (d) => (d ? Math.max(0, Math.round((Date.now() - Date.parse(d)) / 86400000)) : null);

  return (
    <div className="p-6 sm:p-8" data-testid="mech-workorders-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl">Work Orders</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {active.length} open job{active.length === 1 ? "" : "s"} · repairs across the fleet, inventory and customers
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-56">
            <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-10 rounded-full" placeholder="Search WOs, customers…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wo-search" />
          </div>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-pdf-button"><FileUp className="h-4 w-4 mr-2" />Import PDF</Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-wo-button"><Plus className="h-4 w-4 mr-2" />New Work Order</Button>
        </div>
      </div>

      <div className="mb-6">
        <PillTabs
          tabs={[
            { key: "all", label: "All statuses" },
            ...["Open", "In Progress", "Awaiting Parts", "Completed"].map((s) => ({ key: s, label: s })),
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          size="sm"
          testid="wo-filter"
        />
      </div>

      {active.length > 0 && (
        <div className="mb-8">
          <div className="label-mono mb-3">In the shop now</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {active.slice(0, 8).map((w) => {
              const d = daysIn(w.service_date);
              return (
                <QueueCard
                  key={w.id}
                  testid={`wo-queue-${w.id}`}
                  title={w.vehicle_label || "Vehicle"}
                  meta={`${w.complaint || "Service"}${w.lift ? ` · Lift ${w.lift}` : ""}${w.mechanic_name ? ` · ${w.mechanic_name}` : ""}`}
                  figureLabel="In the shop"
                  figure={d === null ? "—" : d === 0 ? "Today" : `${d} day${d === 1 ? "" : "s"}`}
                  urgency={d === null ? "neutral" : d > 7 ? "red" : d > 3 ? "amber" : "green"}
                  badges={<StatusPill status={w.status} tone={STATUS_TONE[w.status] || "neutral"}>{w.status}</StatusPill>}
                  action={
                    <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate(`/mechanic/work-orders/${w.id}`)}>
                      Open work order
                    </Button>
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Mechanic</TableHead>
              <TableHead>Lift</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Cost / Charge</TableHead>
              <TableHead className="text-right">Profit</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">{q ? `No work orders match "${q}"` : "No work orders yet. Create your first one."}</TableCell></TableRow>
            ) : filtered.map((w) => (
              <TableRow key={w.id} className="cursor-pointer" data-testid={`wo-row-${w.id}`} onClick={() => navigate(`/mechanic/work-orders/${w.id}`)}>
                <TableCell className="text-sm whitespace-nowrap">{fmtDate(w.service_date)}</TableCell>
                <TableCell className="font-medium">{w.vehicle_label || "—"}{w.is_oil_change && <Badge className="ml-2 border-0 bg-secondary text-[10px]">OIL</Badge>}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{w.complaint || "—"}</TableCell>
                <TableCell className="text-sm">{w.mechanic_name || "—"}</TableCell>
                <TableCell className="text-sm">{w.lift ? <Badge className="border-0 bg-secondary">L{w.lift}</Badge> : "—"}</TableCell>
                <TableCell><Badge className={`border-0 ${statusStyle[w.status] || "bg-secondary"}`}>{w.status}</Badge></TableCell>
                <TableCell className="text-right font-mono-data">{money(w.totals?.total)}</TableCell>
                <TableCell className="text-right font-mono-data">
                  <span className={w.totals?.profit >= 0 ? "text-green-400" : "text-red-400"}>{money(w.totals?.profit)}</span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/mechanic/work-orders/${w.id}`); }} title="Open full page" data-testid={`open-wo-${w.id}`}><ExternalLink className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditing(w); setOpen(true); }} data-testid={`edit-wo-${w.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this work order?")) delMut.mutate(w.id); }} data-testid={`del-wo-${w.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <WorkOrderDialog open={open} onOpenChange={setOpen} editing={editing} />
      <ImportPdfDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
